import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	TFileId,
	IGetModifierInfoV2,
	TLanguageIDs,
	IItemForGetShortInfo,
	IDefaultLanguage,
	IUpdateModifierV2,
} from 'types';
import { editModifierInitialState, EditModifierContext, TTypeOfModalWindow } from './editModifierContext';
import { editModifierReducer } from './editModifierReducer';
import { EditModifierActionTypes } from './editModifierActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierInfoV2 } from 'backend/modifiers';
import { v4 as uuidv4 } from 'uuid';

export const EditModifierState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editModifierReducer, editModifierInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const modifierID = params.get('modifierID');

	const { response, loading, refetch } = useFetchData<IGetModifierInfoV2, string>(
		modifierID ? getModifierInfoV2 : null,
		modifierID || '',
	);

	useNamedHeader(response?.data.name);

	useEffect(() => {
		if (response) {
			const translations = response.data.translations.map((translation) => ({
				languageId: translation.languageId,
				name: translation.name,
				description: translation.description,
			}));

			dispatch({
				type: EditModifierActionTypes.SET_ALL_DATA,
				payload: {
					modifierID: modifierID || '',
					type: response.data.type,
					isCreatedOnPos: response.data.isCreatedOnPos,
					filePath: response.data.photoPath,
					nameOfModifier: response.data.name,
					description: response.data.description || '',
					initialIDSelectedCustomer: response.data.customer.id,
					posName: response.data.posName || '',
					externalId: response.data.externalId || '',
					articleNumber: response.data.articleNumber || '',
					initialIDsSelectedRestaurant: response.data.restaurants.map((restaurant) => restaurant.id),
					hasSchedulePriority: response.data.overrideScheduleAvailability,
					activationSchedule: response.data.activationCron || '',
					deactivationSchedule: response.data.deactivationCron || '',
					translations,
				},
			});
		}
	}, [modifierID, response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditModifierActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditModifierActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditModifierActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditModifierActionTypes.DELETE_PHOTO }), []);

	const changeNameOfModifier = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_NAME_OF_MODIFIER,
			payload: {
				nameOfModifier: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditModifierActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: EditModifierActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditModifierActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: EditModifierActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditModifierActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: EditModifierActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const toggleHasSchedulePriority = useCallback(() => {
		dispatch({
			type: EditModifierActionTypes.TOGGLE_HAS_SCHEDULE_PRIORITY,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback(({ value, isInitialSet }: { value: string[]; isInitialSet: boolean }) => {
		dispatch({
			type: isInitialSet
				? EditModifierActionTypes.SET_INITIAL_IDS_RESTAURANT
				: EditModifierActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateModifierV2>) => {
		dispatch({
			type: EditModifierActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditModifierContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				setFileId,
				deletePhoto,
				changeNameOfModifier,
				changeDescription,
				setFetchedCustomers,
				setIDCustomer,
				changeExternalId,
				generateUniqueExternalId,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				toggleHasSchedulePriority,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedRestaurants,
				setIDsRestaurant,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				isLoading: state.isLoading,
				filePath: state.filePath,
				fileId: state.fileId,
				modifierID: state.modifierID,
				type: state.type,
				isCreatedOnPos: state.isCreatedOnPos,
				nameOfModifier: state.nameOfModifier,
				description: state.description,
				fetchedCustomers: state.fetchedCustomers,
				initialIDSelectedCustomer: state.initialIDSelectedCustomer,
				IDSelectedCustomer: state.IDSelectedCustomer,
				posName: state.posName,
				externalId: state.externalId,
				articleNumber: state.articleNumber,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				fetchedRestaurants: state.fetchedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditModifierContext.Provider>
	);
};
